import { company } from '~/company/Company';
import { CONTENT_ID } from '~/pages/Root/constants';

interface ResetScrollOptions extends ScrollToOptions {
  isMobile?: boolean;
  /**
   * Set when the scroll reset comes from a history pop rather than from a new
   * navigation or a filter change.
   */
  isHistoryPop?: boolean;
}

/**
 * The element the page actually scrolls, or `null` when that is the document.
 *
 * The V3 desktop layout puts the scrollbar on a `#content` div instead of the
 * document, which changes who owns the scroll position — the browser restores
 * the document across history entries but knows nothing about a nested
 * scroller, so that one has to be remembered by hand.
 */
export const getScrollContainer = (isMobile?: boolean): HTMLElement | null => {
  if (typeof document === 'undefined' || isMobile) {
    return null;
  }

  return company.newDesignSystemEnabled
    ? document.getElementById(CONTENT_ID)
    : null;
};

const resetScroll = (options?: ResetScrollOptions) => {
  const { isMobile, isHistoryPop, left = 0, top = 0, behavior } = options || {};

  if (typeof window === 'undefined') {
    return;
  }

  // The scroll position of a popped entry belongs to whoever restores it — the
  // browser for the document, `useScrollRestoration` for the container it
  // cannot see. Resetting here drops the reader at the top of a list they were
  // halfway down, which is exactly what the restore is undoing.
  if (isHistoryPop) {
    return;
  }

  const container = getScrollContainer(isMobile);

  if (container) {
    container.scrollTo({ left, top, behavior });
    return;
  }

  window.scrollTo({ left, top, behavior });
};

export default resetScroll;
